/**
 * Pagina "Briefing" — digest giornaliero AI su tutto il portafoglio
 * (SPEC-013 §6).
 *
 * Tre punti in cui la UI deve essere esplicita invece che comoda:
 *
 *  1. **`needs_review` si dichiara.** Quando il modello ha violato un vincolo
 *     (un pick che punta a un titolo assente, o un "nuovo" già in portafoglio)
 *     il briefing è salvato lo stesso: nasconderlo darebbe per buono un
 *     contenuto che il backend ha marcato come dubbio.
 *  2. **Un prezzo obiettivo non è tutto uguale.** Quello che viene da
 *     un'analisi di dettaglio è autorevole, quello del digest è una stima:
 *     `priceTargetFromAnalysis` distingue i due, e la pagina lo mostra.
 *  3. **Sono stime, non consulenza.** Il disclaimer sta in fondo alla pagina,
 *     non in un tooltip che si può non aprire mai.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AutoTradingConfirmModal } from '@/features/auto-trading/AutoTradingConfirmModal';
import { ApiError } from '@/lib/api-client';
import { formatDate, formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, Card, EmptyState, Icon, Select, Spinner, TableWrap } from '@/ui';
import {
  useBriefingByDate,
  useBriefingHistory,
  useGenerateBriefing,
  useLatestBriefing,
  useNextBriefingRun,
  type BriefingDetail,
  type BriefingItem,
  type BriefingPick,
  type BriefingRating,
} from './api';
import { useBriefingOutcomes } from '@/features/outcomes/api';
import { OutcomeBadges } from '@/features/outcomes/OutcomeBadges';
import { MarketSentimentCard } from './MarketSentimentCard';
import { describeNextRun } from './next-run-format';
import './briefing.css';
import './briefing-history.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/**
 * L'errore viene dal proxy, non dall'applicazione: la generazione è ancora in
 * corso dall'altra parte (SPEC-013; caso reale dei «504» intermittenti).
 */
function isStillRunning(error: unknown): boolean {
  return error instanceof ApiError && error.isGatewayError;
}

/** Stessa scala e stessi toni dell'analisi di dettaglio: due colori diversi per
 *  lo stesso giudizio renderebbero incomparabili le due schermate. */
const RATING_TONE: Record<BriefingRating, 'positive' | 'negative' | 'neutral' | 'accent'> = {
  Buy: 'positive',
  Overweight: 'positive',
  Hold: 'neutral',
  Underweight: 'negative',
  Sell: 'negative',
};

function RatingBadge({ rating }: { rating: BriefingRating }) {
  return <Badge tone={RATING_TONE[rating]}>{rating}</Badge>;
}

/**
 * Il collegamento allo storico dei giudizi su quel titolo (F-135).
 *
 * ⚠️ **Assente quando il titolo non è a catalogo**, e con scritto perché. Un
 * `new_pick` può nominare una società che la risoluzione non ha agganciato: senza
 * `instrumentId` non c'è niente da interrogare, e un'icona che porta a un 404
 * sarebbe peggio di un'icona che non c'è.
 */
function HistoryLink({ item }: { item: BriefingItem }) {
  if (!item.instrumentId) {
    return (
      <span className="cell--minor" title="Titolo non agganciato al catalogo: non c'è uno storico da mostrare">
        —
      </span>
    );
  }
  return (
    <Link
      className="briefing__history-link"
      to={`/briefing/instrument/${item.instrumentId}`}
      title={`Storico dei giudizi su ${item.symbol}`}
    >
      <Icon name="history" label={`Storico dei giudizi su ${item.symbol}`} />
    </Link>
  );
}

function PriceTarget({ item }: { item: BriefingItem }) {
  if (!item.priceTarget) return <span className="cell--minor">—</span>;
  return (
    <span className="briefing__target">
      {item.priceTarget} {item.priceTargetCurrency ?? ''}
      {item.priceTargetFromAnalysis ? (
        <span className="cell--minor" title="Ripreso dall'analisi di dettaglio del titolo">
          {' '}
          · da analisi
        </span>
      ) : (
        <span className="cell--minor" title="Stimato dal modello del briefing"> · stima</span>
      )}
    </span>
  );
}

function PickCard({ label, pick, tone }: { label: string; pick: BriefingPick; tone: string }) {
  if (!pick) {
    return (
      <Card title={label}>
        {/* Un riferimento mancante non è un vuoto qualsiasi: è la conseguenza
            visibile di un vincolo violato, e va detto. */}
        <p className="cell--minor">Non indicato dal modello, o non risolvibile fra i titoli valutati.</p>
      </Card>
    );
  }
  return (
    <Card title={label}>
      <div className={`briefing__pick briefing__pick--${tone}`}>
        <div className="briefing__pick-head">
          <strong>{pick.item.symbol}</strong>
          <RatingBadge rating={pick.item.rating} />
        </div>
        <div className="cell--minor">{pick.item.name}</div>
        <p>{pick.note}</p>
      </div>
    </Card>
  );
}

function BriefingView({ briefing }: { briefing: BriefingDetail }) {
  // ⚠️ Una chiamata sola per tutta la tabella: un digest ha decine di titoli.
  // La pagina resta viva anche se questa cade — l'esito e' un arricchimento.
  const { data: outcomes } = useBriefingOutcomes(briefing.id);
  if (briefing.status === 'failed') {
    return (
      <Alert variant="error" title={`Briefing del ${formatDate(briefing.briefingDate)} non riuscito`}>
        {briefing.errorMessage ?? 'Errore non specificato.'}
      </Alert>
    );
  }

  return (
    <>
      {briefing.needsReview && (
        <Alert variant="warning" title="Briefing da rivedere">
          Il modello ha violato un vincolo di coerenza: uno dei verdetti punta a un titolo non valutato,
          oppure il titolo indicato come "nuovo" è già in portafoglio. Il contenuto è comunque salvato,
          ma leggilo con attenzione.
        </Alert>
      )}

      <Card
        title={`Sintesi del ${formatDate(briefing.briefingDate)}`}
        subtitle={briefing.generatedAt ? `Generato il ${formatDateTime(briefing.generatedAt)}` : undefined}
      >
        <p className="briefing__summary">{briefing.summary}</p>
      </Card>

      <div className="briefing__picks">
        <PickCard label="Il migliore oggi" pick={briefing.bestPick} tone="best" />
        <PickCard label="Il peggiore oggi" pick={briefing.worstPick} tone="worst" />
        <PickCard label="Nuova idea" pick={briefing.newPick} tone="new" />
      </div>

      <Card title="Titoli valutati" subtitle={`${briefing.items.length} titoli`} flush>
        <TableWrap>
          <table className="table table--compact">
            <thead>
              <tr>
                <th>Titolo</th>
                <th>Giudizio</th>
                <th className="col--secondary">Prezzo obiettivo</th>
                <th className="col--tertiary">Allocazione</th>
                <th>Nota</th>
                {/* F-126: com'e' andato quel giudizio, quando l'orizzonte e' maturato. */}
                <th className="col--secondary">Esito</th>
                <th aria-label="Storico" />
              </tr>
            </thead>
            <tbody>
              {briefing.items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="briefing__name">{item.symbol}</div>
                    <div className="cell--minor">
                      {item.name}
                      {!item.inPortfolio && <> · <Badge tone="neutral">candidato</Badge></>}
                    </div>
                  </td>
                  <td>
                    <RatingBadge rating={item.rating} />
                  </td>
                  <td className="col--secondary">
                    <PriceTarget item={item} />
                  </td>
                  <td className="col--tertiary">
                    {item.recommendedAllocationPct ? (
                      <span className="briefing__target">{item.recommendedAllocationPct}%</span>
                    ) : (
                      <span className="cell--minor">—</span>
                    )}
                  </td>
                  <td>{item.note}</td>
                  <td className="col--secondary">
                    <OutcomeBadges outcomes={outcomes?.byItemId[item.id] ?? []} />
                  </td>
                  <td>
                    <HistoryLink item={item} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      <p className="briefing__disclaimer">
        Prezzi obiettivo e allocazioni sono stime generate da un modello linguistico a partire dai dati
        del tuo portafoglio: non sono consulenza finanziaria.
        {briefing.llmModel && ` Modello: ${briefing.llmModel}.`}
      </p>
    </>
  );
}

/**
 * Quando il digest si generera' da solo (F-111).
 *
 * ## Perche' non basta scrivere un'ora
 *
 * La generazione automatica e' **una cadenza fissa dentro un cancello mobile**:
 * il job gira ogni pochi minuti, ma il digest schedulato aspetta la **prima
 * apertura di un mercato rilevante** — quelli dei titoli che lo alimentano.
 * Un orario secco sarebbe falso il lunedi', quando il cancello sposta tutto
 * all'apertura, e falso il sabato, quando non si apre affatto.
 *
 * ⚠️ **La riga piu' importante e' quella sul digest di oggi.** Se esiste gia',
 * il job salta l'utente fino a mezzanotte UTC: la prossima generazione non e'
 * «fra cinque minuti» ma il prossimo giorno di borsa. Senza dirlo, la pagina
 * lascerebbe credere a un automatismo continuo che si e' invece gia' speso.
 *
 * Non si promettono le **revisioni** di fine giornata: quelle nascono solo se
 * la revisione produce azioni, cioe' da un giudizio su dati che non esistono
 * ancora. Si dice che possono arrivare, non quando.
 */
function NextRunNote() {
  const query = useNextBriefingRun();
  // Silenzio se non si sa: una nota di servizio che sbaglia e' peggio della
  // stessa nota assente.
  if (query.isPending || query.isError || !query.data) return null;

  return <p className="briefing__next-run">{describeNextRun(query.data)}</p>;
}

export function BriefingPage() {
  // Data e revisione vivono nell'URL, non in uno stato locale. Prima ci
  // vivevano, e la conseguenza era che `/briefing?date=…&revision=1` — il
  // collegamento con cui la riconciliazione di un'operazione cita il digest da
  // cui quella decisione e' nata — veniva ignorato in blocco: si atterrava
  // sempre sull'ultimo, senza che nulla lo dicesse. Un indirizzo che non
  // seleziona ciò che nomina non è un indirizzo.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date');
  const revisionParam = Number.parseInt(searchParams.get('revision') ?? '', 10);
  const selectedRevision = Number.isFinite(revisionParam) ? revisionParam : null;

  const setSelection = (date: string | null, revision: number | null) => {
    const next = new URLSearchParams();
    if (date) next.set('date', date);
    if (date && revision) next.set('revision', String(revision));
    // `replace`: scegliere una data non è una tappa da ripercorrere col tasto
    // indietro, che dovrebbe riportare da dove si è arrivati.
    setSearchParams(next, { replace: true });
  };

  const latest = useLatestBriefing();
  const history = useBriefingHistory();
  const byDate = useBriefingByDate(selectedDate, selectedRevision);
  const generate = useGenerateBriefing();
  // Dalla F-057 la generazione manuale passa da una conferma: fino a ieri
  // innescava l'auto-trading da sola, cioè un ordine poteva nascere da un
  // pulsante che dice soltanto «Genera adesso» (SPEC-014.2 §A.3).
  const [confirming, setConfirming] = useState(false);

  /** Rilettura esplicita, per il caso in cui la generazione sia sopravvissuta alla richiesta HTTP. */
  const refreshBriefing = () => Promise.all([latest.refetch(), history.refetch()]);

  const shown = selectedDate ? byDate.data : latest.data?.briefing;
  const loading = selectedDate ? byDate.isLoading : latest.isLoading;

  // Le revisioni si offrono solo dove ce n'è più di una: un selettore con una
  // voce sola sarebbe un comando che non comanda niente.
  const revisions = shown?.revisions ?? [];
  const revisionOptions = revisions.map((entry) => ({
    value: String(entry.revision),
    label:
      `Revisione ${entry.revision}` +
      (entry.generatedAt ? ` — ${formatDateTime(entry.generatedAt)}` : '') +
      (entry.status !== 'completed' ? ` (${entry.status})` : ''),
  }));

  const dateOptions = [
    { value: '', label: 'Ultimo disponibile' },
    ...(history.data?.items ?? []).map((item) => ({
      value: item.briefingDate,
      label: `${formatDate(item.briefingDate)}${item.needsReview ? ' (da rivedere)' : ''}`,
    })),
  ];

  return (
    <div className="briefing">
      {confirming && (
        <AutoTradingConfirmModal
          title="Genera il briefing di oggi"
          summary="Verrà generato il digest su tutte le tue posizioni e sui candidati in wishlist: una chiamata al modello, con un giudizio per titolo e i tre verdetti di sintesi."
          kind="direct"
          confirmLabel="Genera il briefing"
          pending={generate.isPending}
          onClose={() => setConfirming(false)}
          onConfirm={(runAutoTrading) =>
            generate.mutate(runAutoTrading, { onSuccess: () => setConfirming(false) })
          }
        />
      )}
      <header className="page-header">
        <div>
          <h1>Briefing</h1>
          <p className="page-header__subtitle">
            Il digest quotidiano su tutto il portafoglio: un giudizio per titolo, i tre verdetti di
            sintesi e cosa guardare per primo oggi. Si genera da solo prima dell'apertura dei mercati
            per cui hai attivato il trigger.
          </p>
        </div>
        <Button onClick={() => setConfirming(true)} disabled={generate.isPending}>
          {generate.isPending ? 'Generazione…' : 'Genera adesso'}
        </Button>
      </header>

      {/* SPEC-026 §8.4: le due letture del giorno, prima del digest che le usa come contesto. */}
      <MarketSentimentCard />

      {((history.data?.items.length ?? 0) > 1 || revisionOptions.length > 1) && (
        <Card>
          <div className="briefing__selectors">
            {(history.data?.items.length ?? 0) > 1 && (
              <Select
                label="Data"
                value={selectedDate ?? ''}
                // Cambiando giorno la revisione si azzera: il numero di
                // revisione vale dentro una data, e portarselo dietro
                // chiederebbe un documento che in quel giorno può non esistere.
                onChange={(event) => setSelection(event.target.value || null, null)}
                options={dateOptions}
              />
            )}
            {revisionOptions.length > 1 && shown && (
              <Select
                label="Revisione"
                value={String(shown.revision)}
                onChange={(event) =>
                  setSelection(shown.briefingDate, Number.parseInt(event.target.value, 10))
                }
                options={revisionOptions}
                hint="Ogni rigenerazione affianca una revisione invece di sostituire la precedente: è il termine di paragone che dice cosa è cambiato nella giornata."
              />
            )}
          </div>
        </Card>
      )}

      {shown && (
        <p className="briefing__debug-link">
          {/* Piccolo e in fondo alla testata: serve quando un giudizio
              sorprende, non a ogni lettura. */}
          <Link to={`/briefing/${shown.briefingDate}/debug?revision=${shown.revision}`}>
            Come è stato prodotto (prompt e risposta grezza) →
          </Link>
        </p>
      )}

      {generate.isError &&
        (isStillRunning(generate.error) ? (
          // Non è un fallimento: il proxy ha smesso di attendere, il server no.
          // Dirlo «non generato» manderebbe a premere di nuovo il pulsante, cioè
          // a pagare una seconda generazione per un lavoro già in corso.
          <Alert variant="warning" title="La generazione sta continuando sul server">
            Il briefing richiede circa un minuto e mezzo e la risposta non è arrivata in tempo, ma
            l&apos;elaborazione prosegue: fra poco comparirà qui sotto. Ricontrolla fra un minuto
            invece di rigenerarlo, o il modello rifarà da capo un lavoro già avviato.
            <div className="briefing__retry">
              <Button variant="secondary" onClick={() => void refreshBriefing()}>
                Ricontrolla adesso
              </Button>
            </div>
          </Alert>
        ) : (
          <Alert variant="error" title="Briefing non generato">
            {errorMessage(generate.error, 'Errore imprevisto')}
          </Alert>
        ))}

      {loading && <Spinner label="Caricamento del briefing…" />}

      {latest.isError && (
        <Alert variant="error" title="Briefing non disponibile">
          {errorMessage(latest.error, 'Errore imprevisto')}
        </Alert>
      )}

      {!loading && !shown && (
        <EmptyState
          icon="🗞️"
          title="Nessun briefing ancora"
          description="Il briefing si genera automaticamente prima dell'apertura dei mercati con il trigger attivo. Puoi anche chiederlo adesso: serve almeno una posizione in portafoglio o un candidato in wishlist."
          action={
            <Button onClick={() => setConfirming(true)} disabled={generate.isPending}>
              {generate.isPending ? 'Generazione…' : 'Genera adesso'}
            </Button>
          }
        />
      )}

      {shown && <BriefingView briefing={shown} />}

      {/* In fondo di proposito: e' una nota di servizio, non il contenuto —
          chi apre questa pagina viene a leggere il digest, non a sapere quando
          arrivera' il prossimo. */}
      <NextRunNote />
    </div>
  );
}
